import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { pickLocalizedText } from '../utils/localizedContent';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get('token') || '').trim(), [params]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { resetPassword } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const text = (es, en) => pickLocalizedText(lang, es, en);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!token) {
      setError(text('El enlace es inválido o está incompleto.', 'The reset link is invalid or incomplete.'));
      return;
    }
    if (password.length < 8) {
      setError(text('La contraseña debe tener al menos 8 caracteres.', 'Password must be at least 8 characters.'));
      return;
    }
    if (password !== confirmPassword) {
      setError(text('Las contraseñas no coinciden.', 'Passwords do not match.'));
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(text('Contraseña actualizada correctamente.', 'Password updated successfully.'));
      setTimeout(() => navigate('/auth'), 1000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : text('No se pudo actualizar la contraseña.', 'Could not reset password.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-2xl border border-[#C9A962]/30 bg-[#0E1C18]/95 p-6">
        <h1 className="text-2xl text-[#F5F0E6] font-bold mb-2">{text('Nueva contraseña', 'New password')}</h1>
        <p className="text-sm text-[#A8B5AE] mb-6">{text('Introduce tu nueva contraseña para recuperar el acceso.', 'Enter your new password to recover access.')}</p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-[#A8B5AE] mb-1">{text('Contraseña', 'Password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#0A1612] border border-[#C9A962]/20 rounded-xl px-4 py-3 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962]"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-[#A8B5AE] mb-1">{text('Confirmar contraseña', 'Confirm password')}</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full bg-[#0A1612] border border-[#C9A962]/20 rounded-xl px-4 py-3 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962]"
              required
            />
          </div>
          {error && <p className="text-[#C45B4A] text-sm bg-[#C45B4A]/10 px-3 py-2 rounded-lg">{error}</p>}
          {success && <p className="text-[#2D8B70] text-sm bg-[#2D8B70]/10 px-3 py-2 rounded-lg">{success}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary py-3 disabled:opacity-60"
          >
            {loading ? text('Guardando...', 'Saving...') : text('Actualizar contraseña', 'Update password')}
          </button>
        </form>

        <Link to="/auth" className="block text-center mt-4 text-sm text-[#C9A962] hover:text-[#F5F0E6]">
          {text('Volver al acceso', 'Back to login')}
        </Link>
      </div>
    </div>
  );
}
